import ChatIcon from './ChatIcon';
import { useTravelText } from '../../app/TravelLocaleContext';
import { useAnchoredMenuPosition } from '../../components/useAnchoredMenuPosition';
import { useEffect, useId, useRef, useState } from 'react';
import {
  isReactableMessage,
  reactionPickerEmojis,
} from './messageReactionModel';

export default function MessageReactions({
  message,
  canMutate = false,
  busyEmoji = '',
  pickerEmojis = [],
  onToggle,
}) {
  const { t, tf } = useTravelText();
  const pickerId = useId();
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const pickerStyle = useAnchoredMenuPosition(open, triggerRef, pickerRef);
  const reactions = Array.isArray(message?.reactions) ? message.reactions : [];
  const reactable = isReactableMessage(message);
  const emojis = pickerEmojis.length ? pickerEmojis : reactionPickerEmojis(null);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => { if (!canMutate) setOpen(false); }, [canMutate]);
  if (!reactable) return null;
  if (!canMutate && reactions.length === 0) return null;

  return (
    <div className={`travel-msg-reactions${open ? ' is-open' : ''}`} ref={rootRef}>
      {reactions.map((row) => {
        const selected = Boolean(row.reactedByMe);
        const busy = busyEmoji === row.emoji;
        const label = tf(!canMutate ? 'travel.chat.reactionCount' : selected ? 'travel.chat.unreact' : 'travel.chat.react', { emoji: row.emoji, count: row.count });
        return (
          <button
            key={row.emoji}
            type="button"
            className={`travel-msg-reactions__chip${selected ? ' is-mine' : ''}`}
            aria-label={label}
            aria-pressed={selected}
            disabled={!canMutate || busy}
            onClick={() => {
              if (!canMutate) return;
              onToggle?.(row.emoji);
            }}
          >
            <span aria-hidden="true">{row.emoji}</span>
            <span>{row.count}</span>
          </button>
        );
      })}
      {canMutate ? (
        <div className="travel-msg-reactions__add">
          <button
            type="button"
            className="travel-msg-reactions__picker-toggle"
            ref={triggerRef}
            aria-label={t('travel.chat.addReaction')}
            aria-expanded={open}
            aria-controls={pickerId}
            disabled={Boolean(busyEmoji)}
            onClick={() => setOpen((current) => !current)}
          >
            <ChatIcon name="smile" />
          </button>
          {open ? (
            <div ref={pickerRef} style={pickerStyle} id={pickerId} className="travel-msg-reactions__picker" role="listbox" aria-label={t('travel.chat.pickReaction')}>
              {emojis.map((emoji) => {
                const selected = reactions.some((row) => row.emoji === emoji && row.reactedByMe);
                return (
                <button
                  key={emoji}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="travel-msg-reactions__pick"
                  aria-label={tf('travel.chat.pickEmoji', { emoji })}
                  disabled={Boolean(busyEmoji)}
                  onClick={() => {
                    setOpen(false);
                    onToggle?.(emoji);
                  }}
                >
                  {emoji}
                </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
